// src/components/onboarding/OnboardingWizard.jsx
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { supabase } from '../../lib/supabase';
import { useToast } from "../ui/use-toast";
import { ChevronRight, ChevronLeft, Dumbbell, Scale, Target, Check, Info } from 'lucide-react'; // Updated Icon

const OnboardingWizard = ({
  open,
  onOpenChange,
  userId,
  onComplete,
  userPreferences = {}, // Ensure default object
  setUserSettings // Function to update parent's workout settings state
}) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Initialize state with basic defaults, userPreferences will update via useEffect
  const [formData, setFormData] = useState({
    gender: 'male',
    unit: 'imperial',
    weight: '',
    height: { feet: 5, inches: 10, cm: 178 },
    bodyFat: '',
    age: 30,
    activityLevel: 1.375, // Default to lightly active
    activeTemplate: 'stronglifts5x5',
    barWeight: 45,
    plates: [],
    onboardingComplete: true // This flag is set on save
  });

  // *** useEffect to sync internal state with external props when dialog opens ***
  useEffect(() => {
    // Only update form data when the dialog opens and we have preferences to load
    if (open && userPreferences && Object.keys(userPreferences).length > 0) {
      console.log("OnboardingWizard useEffect: Syncing formData with userPreferences:", userPreferences);

      // Determine the unit from preferences, fallback to imperial
      const unitPref = userPreferences.unit || 'imperial';

      // Helper to parse or default height values based on unit
      const getHeightValue = (unit, feet, inches, cm) => {
        if (unit === 'imperial') {
            return {
                feet: parseInt(feet) || 5,
                inches: parseInt(inches) || 10,
                cm: parseInt(cm) || 178 // Keep cm as a fallback if switching
            };
        } else { // Metric
            return {
                feet: parseInt(feet) || 5, // Keep imperial as fallback
                inches: parseInt(inches) || 10,
                cm: parseInt(cm) || 178
            };
        }
      };

      setFormData(prev => ({
        ...prev, // Keep any potentially unsaved user input if needed, though usually want fresh data
        gender: userPreferences.gender || 'male',
        unit: unitPref,
        // Use || '' to avoid showing "null" or "0" if data is missing/invalid
        weight: userPreferences.weight || '',
        height: getHeightValue(unitPref, userPreferences.height_feet, userPreferences.height_inches, userPreferences.height_cm),
        bodyFat: userPreferences.body_fat || '',
        age: parseInt(userPreferences.age) || 30,
        activityLevel: parseFloat(userPreferences.activity_level) || 1.375, // Default to lightly active
        activeTemplate: userPreferences.activeTemplate || 'stronglifts5x5',
        barWeight: unitPref === 'metric' ? 20 : 45,
        // Plates are usually set to default on save, unless explicitly passed (unlikely here)
        plates: userPreferences.plates && userPreferences.plates.length > 0 ? userPreferences.plates : [],
      }));

      // Reset to step 1 when opening
      setStep(1);
    } else if (!open) {
      // Optional: Reset step when dialog closes externally
      setStep(1);
    }
  }, [userPreferences, open]); // Depend on the passed preferences and the open state


  // Handle changes for simple fields
  const handleChange = (field, value) => {
    // Convert age back to number if changed
    const processedValue = field === 'age' ? (parseInt(value) || 0) : value;
     setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));
  };

  // Handle height changes specifically
  const handleHeightChange = (type, value) => {
    setFormData(prev => ({
      ...prev,
      height: {
        ...prev.height,
        // Ensure height values are numbers
        [type]: parseInt(value) || 0
      }
    }));
  };

  // Handle navigation
  const goToNext = () => setStep(prev => Math.min(prev + 1, 4));
  const goToPrevious = () => setStep(prev => Math.max(prev - 1, 1));

  // Save user onboarding data
  const handleComplete = async () => {
    if (!userId) {
      toast({ title: "Error", description: "User ID is missing. Cannot save.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      // --- Prepare data for saving ---
      const unitSystem = formData.unit;
      const weightValue = parseFloat(formData.weight) || null; // Use null if invalid/empty
      const bodyFatValue = parseFloat(formData.bodyFat) || null; // Use null if invalid/empty
      const ageValue = parseInt(formData.age) || null; // Use null if invalid/empty
      const heightFeet = parseInt(formData.height.feet) || 0;
      const heightInches = parseInt(formData.height.inches) || 0;
      const heightCmValue = parseInt(formData.height.cm) || 0;
      const activityLevelValue = parseFloat(formData.activityLevel) || 1.375; // Fallback

      // Calculate height in CM consistently for nutrition calculations and storage
      const heightInCmForCalc = unitSystem === 'imperial'
        ? ((heightFeet * 30.48) + (heightInches * 2.54))
        : heightCmValue;
      // Ensure heightCmValue is updated if the unit is metric but imperial values were edited
      const finalHeightCm = unitSystem === 'metric' ? heightInCmForCalc : heightCmValue;


      // 1. Prepare user_preferences data
      const prefsData = {
        id: userId,
        gender: formData.gender,
        unit: unitSystem,
        // Use calculated cm for the metric height field
        height_cm: finalHeightCm > 0 ? Math.round(finalHeightCm) : null,
         // Only include other fields if they have valid, non-null values
        ...(weightValue !== null && { weight: weightValue }),
        ...(unitSystem === 'imperial' && { height_feet: heightFeet, height_inches: heightInches }),
        ...(bodyFatValue !== null && { body_fat: bodyFatValue }),
        ...(ageValue !== null && { age: ageValue }),
        activity_level: activityLevelValue,
        onboarding_complete: true, // Mark as complete
        updated_at: new Date().toISOString()
      };

      console.log("Upserting user_preferences:", prefsData);
      const { error: prefsError } = await supabase
        .from('user_preferences')
        .upsert(prefsData, { onConflict: 'id' }); // Use onConflict

      if (prefsError) throw prefsError;


      // 2. Prepare and save user_workout_settings
      const defaultPlates = unitSystem === 'imperial'
            ? DEFAULT_PLATES_LBS // Use predefined constants
            : DEFAULT_PLATES_KG;

      const workoutSettingsData = {
        id: userId,
        unit: unitSystem,
        active_template: formData.activeTemplate,
        bar_weight: unitSystem === 'metric' ? 20 : 45, // Consistent with unit
        // Use default plates for onboarding, unless specific plates were passed (very unlikely)
        plates: formData.plates && formData.plates.length > 0 ? formData.plates : defaultPlates,
        updated_at: new Date().toISOString(),
        // Optionally initialize rest times/increments if needed here
         rest_times: { default: 90, exercises: {} }, // Example initialization
         increment_settings: {} // Example initialization
      };

      console.log("Upserting user_workout_settings:", workoutSettingsData);
      const { error: settingsError } = await supabase
        .from('user_workout_settings')
        .upsert(workoutSettingsData, { onConflict: 'id' }); // Use onConflict

      if (settingsError) throw settingsError;


      // 3. Create initial nutrition history entry (if enough data)
      if (weightValue && heightInCmForCalc > 0 && ageValue) {
          let bmr = 0;
          let tdee = 0;
          const weightInKg = unitSystem === 'imperial' ? weightValue * 0.453592 : weightValue;
          const isMale = formData.gender.toLowerCase() === 'male';

          // BMR (Mifflin-St Jeor)
          bmr = (10 * weightInKg) + (6.25 * heightInCmForCalc) - (5 * ageValue) + (isMale ? 5 : -161);
          tdee = bmr * activityLevelValue;

          // Estimate Lean Body Mass (LBM)
          let leanWeightKg = weightInKg; // Start with total weight
           if (bodyFatValue && bodyFatValue > 0 && bodyFatValue < 100) {
               leanWeightKg = weightInKg * (1 - bodyFatValue / 100);
           } else {
                // Rough LBM estimation if BF% is unknown or invalid (using a simplified formula - consider refining)
                // This is a very basic fallback, accuracy varies widely.
                const bmi = weightInKg / ((heightInCmForCalc / 100) ** 2);
                 // Very rough %BF estimation based on BMI and gender (consider Boer formula if more accuracy needed)
                const estimatedBodyFat = isMale
                  ? (1.20 * bmi) + (0.23 * ageValue) - 16.2
                  : (1.20 * bmi) + (0.23 * ageValue) - 5.4;
                 const validEstimatedBodyFat = Math.max(5, Math.min(50, estimatedBodyFat)); // Clamp within reasonable bounds
                 leanWeightKg = weightInKg * (1 - validEstimatedBodyFat / 100);
                 console.warn(`Estimating LBM as BF% was not provided or invalid. Estimated BF%: ${validEstimatedBodyFat.toFixed(1)}%`);
           }

          const leanWeightInLbs = leanWeightKg * 2.20462;
          const proteinMultiplier = 1.0; // Default P multiplier (g/lb LBM)
          const fatMultiplier = 0.4;   // Default F multiplier (g/lb LBM)

          const proteinGrams = Math.max(0, Math.round(leanWeightInLbs * proteinMultiplier));
          const fatGrams = Math.max(0, Math.round(leanWeightInLbs * fatMultiplier));
          const proteinCals = proteinGrams * 4;
          const fatCals = fatGrams * 9;

          // Target calories default to maintenance TDEE for initial setup
          let targetCalories = Math.max(0, Math.round(tdee));
          // Calculate remaining calories for carbs
          const carbCals = Math.max(0, targetCalories - (proteinCals + fatCals));
          const carbGrams = Math.round(carbCals / 4);

          // Prepare nutrition_history data
          const nutritionHistoryData = {
            user_id: userId,
            weight: weightValue,
            // Only include body_fat if it was valid
            ...(bodyFatValue !== null && { body_fat: bodyFatValue }),
            bmr: Math.max(0, Math.round(bmr)),
            tdee: Math.max(0, Math.round(tdee)),
            target_calories: targetCalories,
            goal_type: 'maintenance', // Default goal for initial setup
            protein_grams: proteinGrams,
            carbs_grams: carbGrams,
            fat_grams: fatGrams,
            protein_multiplier: proteinMultiplier,
            fat_multiplier: fatMultiplier,
            activity_level: activityLevelValue,
            // Store consistent height in CM
            height_cm: Math.round(heightInCmForCalc),
            age: ageValue,
            notes: 'Initial setup baseline' // Add a note
            // created_at is handled by db default trigger
          };

          console.log("Inserting initial nutrition_history:", nutritionHistoryData);
          const { error: nutritionError } = await supabase
            .from('nutrition_history')
            .insert(nutritionHistoryData); // Insert, don't upsert initial history

          if (nutritionError) {
              // Log error but don't fail the whole onboarding because of it
              console.error('Error saving initial nutrition history:', nutritionError);
              toast({
                  title: "Warning",
                  description: "Could not save initial nutrition baseline. You can set this via the Calculator.",
                  variant: "destructive",
                  duration: 7000
              });
          }
      } else {
          console.warn("Skipping initial nutrition history save due to missing/invalid data (weight, height, or age).");
          toast({
                title: "Info",
                description: "Initial nutrition baseline skipped (missing weight, height, or age). Use the Calculator to set it up.",
                duration: 7000
            });
      }


      // 4. Update parent component's state (WorkoutPage)
      if (setUserSettings) {
        // Update the workout-specific settings in the parent
        setUserSettings(prev => ({
          ...prev,
          unit: unitSystem,
          activeTemplate: formData.activeTemplate,
          barWeight: workoutSettingsData.bar_weight,
          plates: workoutSettingsData.plates // Reflect actual saved plates
        }));
      }

      // 5. Call the onComplete callback passed from parent
      if (onComplete) {
        // Pass back a representation of the saved state, useful for parent
        onComplete({
            ...prefsData, // Include saved preferences
            ...workoutSettingsData, // Include saved workout settings
             // Ensure onboarding_complete is true in the callback data
             onboarding_complete: true
        });
      }

      toast({
        title: "Setup Complete!",
        description: "Your profile has been configured successfully."
      });

      // 6. Close the dialog
      onOpenChange(false);

    } catch (error) {
      console.error('Error saving onboarding data:', error);
      toast({
        title: "Error Saving Data",
        description: `Failed to save profile: ${error.message || 'Please try again.'}`,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };


  // Render different steps
  const renderStepContent = () => {
    switch(step) {
      case 1: // Basic Info
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <h3 className="text-lg font-medium">Welcome! Basic Info</h3>
              <p className="text-muted-foreground">Let's start with some basic details.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Gender</Label>
                <RadioGroup value={formData.gender} onValueChange={(value) => handleChange('gender', value)} className="flex space-x-4 pt-2">
                  <div className="flex items-center space-x-2"><RadioGroupItem value="male" id="male" /><Label htmlFor="male">Male</Label></div>
                  <div className="flex items-center space-x-2"><RadioGroupItem value="female" id="female" /><Label htmlFor="female">Female</Label></div>
                </RadioGroup>
              </div>
              <div className="space-y-2">
                <Label htmlFor="age">Age</Label>
                <Input id="age" type="number" value={formData.age} onChange={(e) => handleChange('age', e.target.value)} placeholder="Your age" min="1" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Measurement Units</Label>
                <RadioGroup value={formData.unit} onValueChange={(value) => handleChange('unit', value)} className="flex space-x-4 pt-2">
                  <div className="flex items-center space-x-2"><RadioGroupItem value="imperial" id="imperial" /><Label htmlFor="imperial">Imperial (lbs, ft/in)</Label></div>
                  <div className="flex items-center space-x-2"><RadioGroupItem value="metric" id="metric" /><Label htmlFor="metric">Metric (kg, cm)</Label></div>
                </RadioGroup>
              </div>
            </div>
          </div>
        );

      case 2: // Physical Stats
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <h3 className="text-lg font-medium">Your Physical Stats</h3>
              <p className="text-muted-foreground">Helps calculate nutrition needs & workout defaults.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="weight">Current Weight ({formData.unit === 'imperial' ? 'lbs' : 'kg'})</Label>
                <Input id="weight" type="number" value={formData.weight} onChange={(e) => handleChange('weight', e.target.value)} placeholder={formData.unit === 'imperial' ? 'e.g., 180' : 'e.g., 80'} step="0.1" />
              </div>
              <div className="space-y-2">
                <Label>Height</Label>
                {formData.unit === 'imperial' ? (
                  <div className="flex gap-2">
                    <div><Label htmlFor="height-feet" className="text-xs">Feet</Label><Input id="height-feet" type="number" value={formData.height.feet} onChange={(e) => handleHeightChange('feet', e.target.value)} min="0" /></div>
                    <div><Label htmlFor="height-inches" className="text-xs">Inches</Label><Input id="height-inches" type="number" value={formData.height.inches} onChange={(e) => handleHeightChange('inches', e.target.value)} min="0" max="11" /></div>
                  </div>
                ) : (
                  <div><Label htmlFor="height-cm" className="text-xs">Centimeters</Label><Input id="height-cm" type="number" value={formData.height.cm} onChange={(e) => handleHeightChange('cm', e.target.value)} placeholder="Height in cm" min="0" /></div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="bodyFat">Body Fat % (Optional)</Label>
                <Input id="bodyFat" type="number" value={formData.bodyFat} onChange={(e) => handleChange('bodyFat', e.target.value)} placeholder="Enter if known (e.g., 15)" step="0.1" min="0" max="100" />
              </div>
              <div className="space-y-2">
                 <Label>Activity Level</Label>
                 <Select value={String(formData.activityLevel)} onValueChange={(value) => handleChange('activityLevel', parseFloat(value))}>
                    <SelectTrigger><SelectValue placeholder="Select activity level" /></SelectTrigger>
                    <SelectContent>
                        {[
                           { value: 1.2, label: "Sedentary (little/no exercise)" },
                           { value: 1.375, label: "Lightly Active (1-3 days/week)" },
                           { value: 1.55, label: "Moderately Active (3-5 days/week)" },
                           { value: 1.725, label: "Very Active (6-7 days/week)" },
                           { value: 1.9, label: "Extra Active (very hard exercise/job)" }
                        ].map(level => (<SelectItem key={level.value} value={String(level.value)}>{level.label}</SelectItem>))}
                    </SelectContent>
                 </Select>
              </div>
            </div>
          </div>
        );

      case 3: // Workout Program
         // Use predefined constants from WorkoutPage if possible, or define them here
         const WORKOUT_TEMPLATES_WIZARD = {
             'stronglifts5x5': { name: '5x5 Strength (Beginner)', description: 'Build strength with core compound lifts.' },
             'fullBodyHypertrophy': { name: '3-Day Full Body Hypertrophy (Intermediate)', description: 'Focus on muscle growth across the body.' }
             // Add other templates if needed
         };
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <h3 className="text-lg font-medium">Choose Workout Program</h3>
              <p className="text-muted-foreground">Select a starting program. Change or customize later.</p>
            </div>
            <RadioGroup value={formData.activeTemplate} onValueChange={(value) => handleChange('activeTemplate', value)} className="space-y-3">
              {Object.entries(WORKOUT_TEMPLATES_WIZARD).map(([key, template]) => (
                 <Label key={key} htmlFor={key} className="block cursor-pointer">
                    <Card className={`border-2 hover:border-primary/80 ${formData.activeTemplate === key ? 'border-primary shadow-md' : 'border-muted'}`}>
                       <CardContent className="p-4 flex items-center gap-4">
                          <RadioGroupItem value={key} id={key} className="mt-1 self-start"/>
                          <div>
                             <h4 className="font-semibold">{template.name}</h4>
                             <p className="text-sm text-muted-foreground">{template.description}</p>
                          </div>
                       </CardContent>
                    </Card>
                 </Label>
              ))}
            </RadioGroup>
          </div>
        );

      case 4: // Confirmation / Next Steps
        return (
          <div className="space-y-6">
            <div className="space-y-2 text-center">
              <Check className="h-12 w-12 mx-auto text-green-600" />
              <h3 className="text-2xl font-bold">You're All Set!</h3>
              <p className="text-muted-foreground">Your profile is configured. Click "Finish Setup" to begin.</p>
            </div>
            <div className="space-y-3 pt-4 border-t">
               <div className="flex items-start gap-3"><Dumbbell className="h-5 w-5 text-primary flex-shrink-0 mt-1" /><p className="text-sm text-muted-foreground">Go to the <span className='font-medium'>Workout</span> tab to start logging sessions.</p></div>
               <div className="flex items-start gap-3"><Scale className="h-5 w-5 text-primary flex-shrink-0 mt-1" /><p className="text-sm text-muted-foreground">Set initial exercise weights in <span className='font-medium'>Settings</span> if needed.</p></div>
               <div className="flex items-start gap-3"><Target className="h-5 w-5 text-primary flex-shrink-0 mt-1" /><p className="text-sm text-muted-foreground">Use the <span className='font-medium'>Calculator</span> for nutrition insights.</p></div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Define constants for plate weights if not imported
   const DEFAULT_PLATES_LBS = [ { weight: 45, count: 6 }, { weight: 25, count: 6 }, { weight: 10, count: 6 }, { weight: 5, count: 6 }, { weight: 2.5, count: 6 } ];
   const DEFAULT_PLATES_KG = [ { weight: 20, count: 6 }, { weight: 15, count: 2 }, { weight: 10, count: 6 }, { weight: 5, count: 6 }, { weight: 2.5, count: 6 }, { weight: 1.25, count: 6 } ];


  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
         // Prevent closing while loading, allow closing otherwise
         if (!loading) {
             onOpenChange(isOpen);
             if (!isOpen) setStep(1); // Reset step if closed manually
         }
      }}
      // Prevent closing via escape or clicking outside during setup
      onEscapeKeyDown={(e) => e.preventDefault()}
      onInteractOutside={(e) => e.preventDefault()}
    >
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="p-6 pb-4 border-b">
          <DialogTitle>Profile Setup ({step}/4)</DialogTitle>
          {/* Optional: Add a visual progress indicator (e.g., steps component) */}
        </DialogHeader>

        <div className="p-6 space-y-6"> {/* Content area with padding */}
          {renderStepContent()}
        </div>

        <DialogFooter className="p-6 pt-4 border-t flex justify-between w-full">
           {/* Back Button */}
           {step > 1 ? (
            <Button variant="outline" onClick={goToPrevious} className="flex items-center gap-1" disabled={loading}>
              <ChevronLeft className="h-4 w-4" /> Back
            </Button>
          ) : ( <div /> /* Placeholder */ )}

           {/* Next / Finish Button */}
          {step < 4 ? (
            <Button onClick={goToNext} className="flex items-center gap-1" disabled={loading}>
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={handleComplete} disabled={loading} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white">
              {loading ? "Saving..." : "Finish Setup"}
              {!loading && <Check className="h-4 w-4" />}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingWizard;